import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MailIcon from "@mui/icons-material/Mail";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HomeIcon from "@mui/icons-material/Home";
import LocalNavbar from "./LocalComponents/LocalNavbar";
import Eye from "../../../GlobalComponents/Eye";
import synnexLogo from "../../../Assets/Images/Illustrations/synnex-logo.png";
import copyIcon from "../../../Assets/Images/Icons/copy.svg";
import loadingGif from "../../../Assets/Images/Animations/loading-management-console.gif";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import invalidIllustration from "../../../Assets/Images/Illustrations/delete-card-img.svg";
import styles from "./loginCredentialsTemporaryPasswordTemporaryPassword.module.scss";
import { cloudManagerApi } from "../../../Utils/API";
import { GET_ADMIN_PORTAL_CREDENTIALS, MARK_CREDENTIAL_USED } from "../../../Utils/GlobalConstants";

// API response shape: { username: string, password: string, url: string }
// `url` is the actual portal href

const LoginCredentialsTemporaryPassword = () => {
  // Raw token extraction — preserves + characters in Base64 tokens.
  const rawQuery = window.location.search;
  const tokenMatch = rawQuery.match(/[?&]token=([^&]*)/);
  const token = tokenMatch ? tokenMatch[1] : null;

  // "loading" | "credentials" | "expired" | "invalid"
  const [pageState, setPageState] = useState("loading");
  const [credentials, setCredentials] = useState(null);

  const [passwordType, setPasswordType] = useState(["password", ""]);
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!token) {
      setPageState("expired");
      return;
    }

    cloudManagerApi
      .get(GET_ADMIN_PORTAL_CREDENTIALS + token)
      .then((resp) => {
        const data = resp.data;
        if (data && data.isActive === false) {
          setPageState("expired");
        } else {
          setCredentials(data);
          setPageState("credentials");

          cloudManagerApi
            .post(MARK_CREDENTIAL_USED, {
              token: token,
              username: data.username,
            })
            .catch(() => {});
        }
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 400) {
          setPageState("invalid");
        } else {
          setPageState("expired");
        }
      });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = (value, fieldKey) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // ── Loading State ─────────────────────────────────────────────────────────
  const renderLoading = () => (
    <div className={styles["loading-container"]}>
      <img src={loadingGif} alt="Loading..." className={styles["loading-gif"]} />
    </div>
  );

  // ── "What can I do?" shared section ──────────────────────────────────────
  // Icons wrapped in <span> so SCSS targets svg path { fill } for reliable colour.
  const renderWhatCanIDo = (line1Node) => (
    <div className={styles["wcd-section"]}>
      <hr className={styles["wcd-divider"]} />
      <h3 className={styles["wcd-title"]}>What can I do?</h3>

      <div className={styles["wcd-item"]}>
        <span className={styles["wcd-icon--blue"]}>
          <MailIcon style={{ fontSize: 20 }} />
        </span>
        <p className={styles["wcd-item-text"]}>{line1Node}</p>
      </div>

      <div className={styles["wcd-item"]}>
        <span className={styles["wcd-icon--blue"]}>
          <SupportAgentIcon style={{ fontSize: 20 }} />
        </span>
        <p className={styles["wcd-item-text"]}>
          Contact{" "}
            <strong>online support</strong>
          {" "}
          if you continue to experience issues.
        </p>
      </div>

      <div className={styles["wcd-actions"]}>
        <a
          href="mailto:online-support@au.synnex-grp.com"
          className={styles["wcd-btn-primary"]}
        >
          <span className={styles["wcd-icon--white"]}>
            <SupportAgentIcon style={{ fontSize: 18 }} />
          </span>
          Contact Support
        </a>
        <a href="/" className={styles["wcd-btn-secondary"]}>
          <span className={styles["wcd-icon--blue"]}>
            <HomeIcon style={{ fontSize: 18 }} />
          </span>
          Go to Homepage
        </a>
      </div>
    </div>
  );

  // ── Expired / Already Viewed State ────────────────────────────────────────
  const renderExpired = () => (
    <div className={styles["state-card"]}>
      {/* Red circle — same 9rem visual size as icon circles */}
      <div className={styles["icon-circle--red"]}>
        <AccessTimeIcon style={{ fontSize: "2.75rem" }} />
      </div>
      <h2 className={styles["state-title"]}>The link is expired</h2>
      <p className={styles["state-body"]}>
        This account setup link is no longer valid. Links expire after{" "}
        <strong>72 hours</strong> or immediately after being viewed for the
        first time.
      </p>
      {renderWhatCanIDo(
        <>
          Ask your Administrator to{" "}
          <strong>resend the credentials link</strong> to your registered email.
        </>
      )}
    </div>
  );

  // ── Invalid Token State ───────────────────────────────────────────────────
  const renderInvalid = () => (
    <div className={styles["state-card"]}>
      <img
        src={invalidIllustration}
        alt="Invalid link"
        className={styles["state-illustration"]}
      />
      <h2 className={styles["state-title"]}>The link is invalid</h2>
      <p className={styles["state-body"]}>
        The password reset link you followed is not valid.
      </p>
      {renderWhatCanIDo(
        <>
          Use the <strong>exact link</strong> from your password reset email.
        </>
      )}
    </div>
  );

  // ── Credential Row ────────────────────────────────────────────────────────
  const CredentialRow = ({ label, value, fieldKey, isPassword = false }) => {
    const displayValue =
      isPassword && passwordType[0] === "password"
        ? "•".repeat(value.length)
        : value;

    return (
      <div className={styles["credential-row"]}>
        <span className={styles["credential-label"]}>{label}</span>
        <div className={styles["credential-value-wrap"]}>
          <span className={styles["credential-value"]}>{displayValue}</span>
          <div className={styles["credential-actions"]}>
            {isPassword && (
              <Eye
                passwordType={passwordType}
                setPasswordType={setPasswordType}
              />
            )}
            <button
              className={`${styles["copy-icon-btn"]} ${
                copiedField === fieldKey ? styles["copy-icon-btn--copied"] : ""
              }`}
              onClick={() => handleCopy(value, fieldKey)}
              title={`Copy ${label}`}
              aria-label={`Copy ${label} to clipboard`}
            >
              {copiedField === fieldKey ? (
                <CheckIcon style={{ fontSize: 18, color: "#1D874C" }} />
              ) : (
                <img src={copyIcon} alt="copy" className={styles["copy-icon-img"]} />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Credentials View ──────────────────────────────────────────────────────
  const renderCredentials = () => (
    <>
      {/* Hero */}
      <div className={styles["hero-section"]}>
        <img src={synnexLogo} alt="Synnex" className={styles["hero-logo"]} />
        <h1 className={styles["hero-title"]}>Login Credentials</h1>
        <p className={styles["hero-subtitle"]}>
          Your cloud account has been successfully created.
        </p>
      </div>

      {/* Credential Card */}
      <div className={styles["card"]}>
        <div className={styles["card-header"]}>
          <LockOutlinedIcon className={styles["card-header-icon"]} />
          <span className={styles["card-header-title"]}>Account Credentials</span>
        </div>

        <div className={styles["card-body"]}>
          <CredentialRow
            label="Username"
            value={credentials.username}
            fieldKey="username"
          />

          <div className={styles["credential-divider"]} />

          <CredentialRow
            label="Temporary Password"
            value={credentials.password}
            fieldKey="password"
            isPassword
          />

          <div className={styles["credential-divider"]} />

          <div className={styles["credential-row"]}>
            <span className={styles["credential-label"]}>Login URL</span>
            <div className={`${styles["credential-value-wrap"]} ${styles["partner-link-btn-wrap"]}`}>
              {credentials.url ? (
                <a
                  href={credentials.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  title={credentials.url}
                  style={{ textDecoration: "none", fontSize: "0.875rem" }}
                >
                  <span className={styles["partner-link-url"]}>
                    {credentials.url}
                  </span>
                  <OpenInNewIcon style={{ fontSize: 16, color: "#fff" }} />
                </a>
              ) : (
                <span className={styles["partner-link-pending"]}>
                  Link will be available shortly
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={styles["expiry-note"]}>
          <span className={styles["expiry-note-label"]}>Note: </span>
          This link is valid for <strong>72 hours only</strong> or until
          viewed for the first time, whichever comes first. Please save your
          credentials in a secure location before this page expires.
        </div>
      </div>
    </>
  );

  // ── Main Render ───────────────────────────────────────────────────────────
  // Single Col — width adapts per state to avoid empty sibling cols.
  const isCredentials = pageState === "credentials" && credentials;
  const isError = pageState === "expired" || pageState === "invalid";

  return (
    <div className={styles["page-wrapper"]}>
      <LocalNavbar />

      <div
        className={`${styles["page-content"]} ${
          pageState === "loading" ? styles["page-content--loading"] : ""
        } ${isError ? styles["page-content--centered"] : ""}`}
      >
        <Container>
          <Row className="justify-content-center">
            <Col
              xs={12}
              sm={isCredentials ? 11 : 10}
              md={isCredentials ? 10 : 8}
              lg={isCredentials ? 8 : 6}
              xl={isCredentials ? 7 : 5}
            >
              {pageState === "loading" && renderLoading()}
              {pageState === "expired" && renderExpired()}
              {pageState === "invalid" && renderInvalid()}
              {isCredentials && renderCredentials()}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default LoginCredentialsTemporaryPassword;
